import React, { useEffect, useState } from "react";
import styles from "./boxRanger.module.scss";
import Slider, { SliderThumb } from "@mui/material/Slider";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import NumberFormat from "react-number-format";
import { getOnlyNumbers } from "../../common/utils/Utils";

export interface BoxRangerProps {
  children?: any;
  maxRanger: number;
  minRanger: number;
  onChange?: any;
  limitValue?: number;
  type: "date" | "price";
  defaultValue?: Array<number> | number;
  classname?: string;
  readyOnly?: boolean;
}

export const TEST_ID = "box-ranger-testid";

const BidSlider = styled(Slider)(({ theme }) => ({
  color: "#1283c2",
  height: 3,
  padding: "13px 0",
  "& .MuiSlider-thumb": {
    height: 20,
    width: 20,
    backgroundColor: "#fff",
    border: "1px solid currentColor",
    "&:hover": {
      boxShadow: "0 0 0 8px rgba(18, 131, 194, 0.16)",
    },
    "& .airbnb-bar": {
      height: 9,
      width: 1,
      backgroundColor: "currentColor",
      marginLeft: 1,
      marginRight: 1,
    },
  },
  "& .MuiSlider-track": {
    height: 4,
  },
  "& .MuiSlider-rail": {
    color: theme.palette.mode === "dark" ? "#bfbfbf" : "#d8d8d8",
    opacity: theme.palette.mode === "dark" ? undefined : 1,
    height: 3,
  },
}));

function BidSliderThumb(props) {
  const { children, ...other } = props;
  return (
    <SliderThumb {...other}>
      {children}
      <span className="airbnb-bar" />
      <span className="airbnb-bar" />
    </SliderThumb>
  );
}

const calculateStepDifference = (sliderValue: number[]): number => {
  const minValue = sliderValue[0];
  const maxValue = sliderValue[1];

  let result = Math.abs(maxValue - minValue);

  if (result < 2022) return 1;
  if (result < 1000) return 10;
  if (result > 1000) return 100;
};

export const BoxRanger: React.FC<BoxRangerProps> = ({
  children,
  maxRanger,
  minRanger,
  onChange,
  type,
  defaultValue,
  classname,
  limitValue,
  readyOnly,
}) => {
  const [minNumber, setMinNumber] = useState(minRanger);
  const [maxNumber, setMaxNumber] = useState(maxRanger);
  const [value, setValue] = useState<number[]>([minNumber, maxNumber]);

  const withValueLimit = ({ floatValue }) => floatValue <= limitValue;

  const valuetext = (value: number) => {
    return `${value}`;
  };

  const handleChange = (event: Event, newValue: number | number[]) => {
    setValue(newValue as number[]);
  };

  useEffect(() => {
    if (defaultValue) setValue(defaultValue as number[]);
  }, [defaultValue]);

  const handleNumberFormat = (type = "price", value, value2) => {
    const price =
      type === "price"
        ? {
            prefix: "R$",
            decimalScale: 2,
            decimalSeparator: ",",
            thousandSeparator: ".",
            value: Intl.NumberFormat("pt-BR", {
              style: "currency",
              currency: "BRL",
              maximumFractionDigits: 2,
            }).format(value),
          }
        : {
            value: value,
          };

    return (
      <NumberFormat
        className={styles.box__info__number}
        {...price}
        onChange={(e: { target: { value: any } }) => {
          setValue([e.target.value || 0, value2]);
          onChange({ min: e.target.value, max: value2 });
        }}
        isAllowed={withValueLimit}
        readOnly={readyOnly}
      />
    );
  };

  return (
    <div data-testid={TEST_ID} className={[classname, styles.box].join(" ")}>
      <div className={styles.box__info}>
        {handleNumberFormat(type, value[0], value[1])}
        {handleNumberFormat(type, value[1], value[0])}
      </div>

      <div style={{ width: "100%" }}>
        <BidSlider
          components={{ Thumb: BidSliderThumb }}
          getAriaLabel={() => "MultSlideRanger"}
          value={value}
          onChange={(event, value: any) => {
            handleChange(event, value);
            onChange({ min: value[0], max: value[1] });
          }}
          valueLabelDisplay="off"
          getAriaValueText={valuetext}
          min={minNumber}
          max={maxNumber}
          step={calculateStepDifference(value)}
          defaultValue={defaultValue && defaultValue}
        />
      </div>
    </div>
  );
};
